"use client";

import { useMemo, useState } from "react";
import { ChevronLeft, Link2, Plus } from "lucide-react";
import { Button } from "@repo/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui";
import { useEntityInstanceList } from "@/hooks/use-entity-instance-list";
import { useDebounce } from "@/lib/client-utils";
import type { MediaUsageRelation } from "@/services/pylo-media.service";
import type { UsedByFilter } from "@/lib/media-library-filter";

interface UsedByFilterPopoverProps {
  relations: Array<MediaUsageRelation>;
  onSelect: (usedBy: UsedByFilter) => void;
}

const InstancePicker = ({
  relation,
  onBack,
  onSelect,
}: {
  relation: MediaUsageRelation;
  onBack: () => void;
  onSelect: (usedBy: UsedByFilter) => void;
}) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const filter = useMemo(
    () => ({
      sortby: [{ field: "created_at", order: "desc" as const }],
      query: debouncedSearch.trim()
        ? [
            {
              condition: {
                field: relation.displayField,
                operator: "ilike" as const,
                value: `%${debouncedSearch.trim()}%`,
              },
            },
          ]
        : undefined,
    }),
    [debouncedSearch, relation.displayField],
  );

  const instances = useEntityInstanceList({
    entityName: relation.entityName,
    filter,
    fetchAllFields: false,
    fieldsToFetch: ["id", relation.displayField],
  });

  const rows = useMemo(
    () =>
      instances.data?.pages.flatMap(
        (page) => page.entityInstances as Array<Record<string, unknown>>,
      ) ?? [],
    [instances.data],
  );

  return (
    <Command shouldFilter={false}>
      <div className="flex items-center gap-1 border-b px-2 py-1.5">
        <Button variant="ghost" size="sm" className="h-7 gap-1 px-1.5" onClick={onBack}>
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <span className="text-[13px] font-medium">{relation.entityName}</span>
      </div>
      <CommandInput
        placeholder={`Search ${relation.entityName}…`}
        value={search}
        onValueChange={setSearch}
      />
      <CommandList className="max-h-64">
        <CommandItem
          value="__any__"
          onSelect={() =>
            onSelect({
              source: relation.source,
              fieldName: relation.fieldName,
              entityName: relation.entityName,
            })
          }
          className="text-[13px]"
        >
          <Link2 className="mr-2 h-3.5 w-3.5" />
          Any {relation.entityName}
        </CommandItem>
        {!instances.isLoading && rows.length === 0 && (
          <CommandEmpty>No records found.</CommandEmpty>
        )}
        <CommandGroup>
          {rows.map((row) => {
            const id = String(row.id);
            const label = String(row[relation.displayField] ?? id);
            return (
              <CommandItem
                key={id}
                value={id}
                className="text-[13px]"
                onSelect={() =>
                  onSelect({
                    source: relation.source,
                    fieldName: relation.fieldName,
                    entityName: relation.entityName,
                    instanceId: id,
                    instanceLabel: label,
                  })
                }
              >
                {label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

export const UsedByFilterPopover = ({ relations, onSelect }: UsedByFilterPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [relation, setRelation] = useState<MediaUsageRelation | null>(null);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setRelation(null);
  };

  const handleSelect = (usedBy: UsedByFilter) => {
    onSelect(usedBy);
    handleOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-[13px]">
          <Plus className="h-3.5 w-3.5" />
          Used by
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        {relation ? (
          <InstancePicker
            relation={relation}
            onBack={() => setRelation(null)}
            onSelect={handleSelect}
          />
        ) : (
          <Command>
            <CommandInput placeholder="Search records…" />
            <CommandList className="max-h-64">
              {relations.length === 0 ? (
                <div className="px-3 py-6 text-center text-[13px] text-muted-foreground">
                  No entity links a file back to itself yet.
                </div>
              ) : (
                <>
                  <CommandEmpty>No match.</CommandEmpty>
                  <CommandGroup>
                    {relations.map((item) => (
                      <CommandItem
                        key={`${item.source}:${item.entityName}:${item.fieldName}`}
                        value={`${item.entityName} ${item.fieldName}`}
                        className="text-[13px]"
                        onSelect={() => setRelation(item)}
                      >
                        {item.entityName}
                        <span className="ml-auto text-[11px] text-muted-foreground">
                          {item.fieldName}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
};
